// General utility types
export type HasKey<K extends PropertyKey> = {
  [P in K]: unknown;
};
export type UnionKeys<T> = T extends any ? keyof T : never;
export type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Mutable<T> = { -readonly [P in keyof T]: T[P] };
export type ValueOf<T> = T[keyof T];
export type ValuesOf<T extends readonly any[]> = T[number];
export type Explicit<T> = [T][T extends any ? 0 : never];
export type SubsetOf<T, U extends T> = U;
export type PartialRecord<K extends keyof any, T> = { [P in K]?: T };
export type Modify<T, S> = Omit<T, keyof S> & S;
export type NestedPartial<T> = {
  [attr in keyof T]?: T[attr] extends object ? NestedPartial<T[attr]> : T[attr];
};
export type NullablePartial<T> = {
  [P in keyof T]?: T[P] | null;
};
export type Extends<T, U extends T> = U;
export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends object | undefined ? RecursivePartial<NonNullable<T[P]>> | null : T[P] | null;
};

export type SaveState = "NOT_SAVED" | "SAVING" | "SAVED";

export enum SaveStatus {
  // normal states
  SAVED = "SAVED",
  PRE_SAVE = "PRE_SAVE",
  SAVING = "SAVING",
  // error states
  ERROR = "ERROR",
  OFFLINE = "OFFLINE",
  CONFLICT = "CONFLICT",
}

// Useful when trying to use "as const" alongside a type

export const enforceType = <T>(obj: T): T => obj;

// Usage: IgnoreBuiltins<keyof SomeType> to create a type referencing
// all additional properties on SomeType

export type IgnoreBuiltins<S extends string> = string extends S
  ? string
  : S extends ""
  ? S
  : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  S extends `$${infer T}`
  ? never
  : S;
/**
 * Gets the keys of a given type `U` from an object type `T`.
 * @template T - The object type.
 * @template U - The type to match against the object's values.
 * @returns The keys of type `U` from the object type `T`.
 */
export type KeysOfType<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never;
}[keyof T];

/**
 * Utility type that converts specified string keys to number types in a generic type T
 * Only converts keys that exist in T and are of type string or string | undefined
 * @template T The input type to transform
 * @template K The union of keys to attempt to convert from string to number
 */
export type ConvertStringKeysToNumber<T, K> = {
  [P in keyof T]: P extends K & keyof T
    ? T[P] extends string
      ? number
      : T[P] extends string | undefined
      ? number | undefined
      : T[P]
    : T[P];
};
